import { useQuery } from '@tanstack/react-query';
import { queryKeyFactory } from 'utils/query-client';
import { loaders } from './employees.loaders';
import { converters } from './employees.converters';
import {
  EmployeeExpensesFilters,
  EmployeeJobTitlesFilters,
  EmployeeWalletBenefitsFilters,
  EmployeesFilters,
} from './employees.types';

export const employeesKeys = queryKeyFactory('employees');

export const useEmployees = (
  filters: Partial<EmployeesFilters> = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: employeesKeys.list(filters),
    queryFn: () => loaders.getEmployees({ filters }),
    select: converters.getEmployees,
    ...options,
  });
};

export const useEmployee = (employeeId: string) => {
  return useQuery({
    queryKey: employeesKeys.detail(employeeId),
    queryFn: () => loaders.getEmployee({ employeeId }),
    select: converters.getEmployee,
    enabled: !!employeeId,
  });
};

export const useEmployeeExpenses = (
  employeeId: string,
  filters: Partial<EmployeeExpensesFilters> = {},
) => {
  return useQuery({
    queryKey: employeesKeys.detail(employeeId, 'expenses', filters),
    queryFn: () => loaders.getExpenses({ employeeId, filters }),
    select: converters.getExpenses,
    enabled: !!employeeId,
  });
};

export const useEmployeeWalletBenefits = (
  employeeId: string,
  filters: Partial<EmployeeWalletBenefitsFilters> = {},
) => {
  return useQuery({
    queryKey: employeesKeys.detail(employeeId, 'wallet-benefits', filters),
    queryFn: () => loaders.getWalletBenefits({ employeeId, filters }),
    select: converters.getWalletBenefits,
    enabled: !!employeeId,
  });
};

export const useEmployeeWalletRecognitions = (employeeId: string) => {
  return useQuery({
    queryKey: employeesKeys.detail(employeeId, 'wallet-recognitions'),
    queryFn: () => loaders.getWalletRecognitions({ employeeId }),
    select: converters.getWalletRecognitions,
    enabled: !!employeeId,
  });
};

export const useEmployeeJobTitles = (
  filters: Partial<EmployeeJobTitlesFilters> = {},
) => {
  return useQuery({
    queryKey: employeesKeys.list('job-titles', filters),
    queryFn: () => loaders.getJobTitles({ filters }),
    select: converters.getJobTitles,
  });
};

export const useEmployeeUniqueJobTitles = () => {
  return useQuery({
    queryKey: employeesKeys.list('unique-job-titles'),
    queryFn: () => loaders.getUniqueJobTitles(),
    select: converters.getUniqueJobTitles,
  });
};
